/**
 * Formatting shared by the Telna pages.
 *
 * Kept out of the components because the same allowance, the same status and
 * the same ICCID appear in the catalogue table, the inventory table, the order
 * table and the order drawer — four places that had better agree on what
 * "1 GB" means.
 */

const GB = 1024 ** 3;
const MB = 1024 ** 2;

/** Telna reports every allowance in bytes. Unlimited is expressed as 0/null. */
export const formatBytes = (bytes) => {
    if (bytes === null || bytes === undefined) return '—';
    if (!bytes) return 'Unlimited';
    if (bytes >= GB) {
        const gb = bytes / GB;
        return `${gb.toFixed(gb % 1 === 0 ? 0 : 2)} GB`;
    }
    return `${Math.round(bytes / MB)} MB`;
};

export const formatDate = (d) => (d ? new Date(d).toLocaleString() : '—');
export const formatDateShort = (d) => (d ? new Date(d).toLocaleDateString() : '—');

/** Telna prices nothing, so the panel prints whatever a human stored. */
export const formatMoney = (amount, currency = 'EUR') => {
    if (amount === null || amount === undefined) return 'Unpriced';
    const symbol = currency === 'USD' ? '$' : currency === 'GBP' ? '£' : '€';
    return `${symbol}${Number(amount).toFixed(2)}`;
};

/** Order status -> antd Tag colour. */
export const ORDER_STATUS_COLOR = {
    draft: 'default',
    provisioning: 'blue',
    completed: 'green',
    // Amber, not green: an order owing two eSIMs and holding one is a support
    // ticket waiting to happen and must not look finished.
    partial: 'orange',
    failed: 'red',
    cancelled: 'default',
};

/** Order line status -> colour. */
export const ITEM_STATUS_COLOR = {
    pending: 'default',
    reserved: 'blue',
    completed: 'green',
    failed: 'red',
};

/** Telna SIM status -> colour. `pre-service` is the sellable one. */
export const SIM_STATUS_COLOR = {
    'pre-service': 'green',
    'in-service': 'blue',
    'waiting-for-assignment': 'default',
    terminated: 'red',
};

/** eUICC download state -> colour. Tracks the customer's device, not the plan. */
export const PROFILE_STATE_COLOR = {
    RELEASED: 'default',
    DOWNLOADED: 'blue',
    INSTALLED: 'cyan',
    ENABLED: 'green',
    DISABLED: 'orange',
    ERROR: 'red',
    DELETED: 'red',
};

/** Package status -> colour. */
export const PACKAGE_STATUS_COLOR = {
    NOT_ACTIVE: 'gold',
    ACTIVE: 'green',
    TERMINATED: 'default',
};

/**
 * Telna templates express validity as `{unit, duration}` where unit is
 * CALENDAR_MONTH or SECOND. Days is the only unit a human wants to read.
 */
export const formatValidity = (template) => {
    if (template?.validityDays) return `${template.validityDays} days`;
    const v = template?.validity;
    if (!v || v.duration === null || v.duration === undefined) return '—';
    if (v.unit === 'CALENDAR_MONTH') return `${v.duration} month${v.duration === 1 ? '' : 's'}`;
    if (v.unit === 'SECOND') return `${Math.round(v.duration / 86400)} days`;
    return `${v.duration} ${v.unit}`;
};

/** Seconds -> "30 days", for the activation window. */
export const formatSeconds = (seconds) => {
    if (!seconds) return '—';
    const days = Math.round(seconds / 86400);
    return days >= 1 ? `${days} days` : `${Math.round(seconds / 3600)} hours`;
};
